import { batch } from 'react-redux';
import { createRecordingEvent } from '../analytics/AnalyticsEvents';
import { sendAnalytics } from '../analytics/functions';
import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../base/app/actionTypes';
import { CONFERENCE_JOIN_IN_PROGRESS, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import { openDialog } from '../base/dialog/actions';
import JitsiMeetJS, { JitsiConferenceEvents, JitsiRecordingConstants } from '../base/lib-jitsi-meet';
import { setAudioMuted, setAudioUnmutePermissions, setVideoMuted, setVideoUnmutePermissions } from '../base/media/actions';
import { MEDIA_TYPE } from '../base/media/constants';
import { PARTICIPANT_JOINED, PARTICIPANT_LEFT, PARTICIPANT_UPDATED } from '../base/participants/actionTypes';
import { updateLocalRecordingStatus } from '../base/participants/actions';
import { PARTICIPANT_ROLE } from '../base/participants/constants';
import { getLocalParticipant, getParticipantDisplayName, getParticipantCount, isLocalParticipantModerator } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import StateListenerRegistry from '../base/redux/StateListenerRegistry';
import { playSound, stopSound } from '../base/sounds/actions';
import { TRACK_ADDED } from '../base/tracks/actionTypes';
import { hideNotification, showErrorNotification, showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { isRecorderTranscriptionsRunning } from '../transcribing/functions';
import { RECORDING_SESSION_UPDATED, START_LOCAL_RECORDING, STOP_LOCAL_RECORDING } from './actionTypes';
import { clearRecordingSessions, hidePendingRecordingNotification, markConsentRequested, showPendingRecordingNotification, showRecordingError, showRecordingLimitNotification, showRecordingWarning, showStartRecordingNotification, showStartedRecordingNotification, showStoppedRecordingNotification, updateRecordingSessionData } from './actions';
import { RecordingConsentDialog } from './components/Recording';
import LocalRecordingManager from './components/Recording/LocalRecordingManager';
import { LIVE_STREAMING_OFF_SOUND_ID, LIVE_STREAMING_ON_SOUND_ID, RECORDING_OFF_SOUND_ID, RECORDING_ON_SOUND_ID, START_RECORDING_NOTIFICATION_ID, RECORDING_ANNOUNCEMENT_NOTIFICATION_ID } from './constants';
import { getResourceId, getSessionById, getActiveSession, get11SightRecordingMetadata, registerRecordingAudioFiles, shouldRequireRecordingConsent, unregisterRecordingAudioFiles } from './functions';
import logger from './logger';
// Recording Guard & Auto-Recording Constants
const RECORDING_MAX_DURATION_MS = 120 * 60 * 1000;
const RECORDING_GUARD_INTERVAL_MS = 60 * 1000;
let recordingGuardInterval;
let activeRecordingKey;
function getMeetingIdSafely(conference) {
    return conference?._room?.room?.meetingId || window.APP?.room?.id || 'default';
}
function getRecordingStorageKey(meetingId) {
    return `__11sight_recording_start_${meetingId}`;
}
function startRecordingGuard(dispatch, getState) {
    if (recordingGuardInterval) {
        clearInterval(recordingGuardInterval);
    }
    recordingGuardInterval = window.setInterval(() => {
        ensureRecordingWithinLimit(dispatch, getState);
    }, RECORDING_GUARD_INTERVAL_MS);
    console.log('[Recording Guard] Started recording duration checks.');
}
function stopRecordingGuard() {
    if (recordingGuardInterval) {
        clearInterval(recordingGuardInterval);
        recordingGuardInterval = undefined;
    }
    console.log('[Recording Guard] Stopped recording duration checks.');
}
function registerRecordingStart(dispatch, getState, sessionId) {
    if (typeof localStorage === 'undefined') {
        return;
    }
    const state = getState();
    const conference = getCurrentConference(state);
    const meetingId = getMeetingIdSafely(conference);
    const key = getRecordingStorageKey(meetingId);
    activeRecordingKey = key;
    const payload = {
        start: Date.now(),
        sessionId
    };
    localStorage.setItem(key, JSON.stringify(payload));
    console.log(`[Recording Guard] Registered recording start for key: ${key}`, payload);
    startRecordingGuard(dispatch, getState);
}
function clearRecordingState() {
    if (typeof localStorage !== 'undefined' && activeRecordingKey) {
        localStorage.removeItem(activeRecordingKey);
        console.log(`[Recording Guard] Cleared storage state for key: ${activeRecordingKey}`);
    }
    activeRecordingKey = undefined;
    stopRecordingGuard();
}
function ensureRecordingWithinLimit(dispatch, getState) {
    if (typeof localStorage === 'undefined' || !activeRecordingKey) {
        return;
    }
    const raw = localStorage.getItem(activeRecordingKey);
    if (!raw) {
        clearRecordingState();
        return;
    }
    let info;
    try {
        info = JSON.parse(raw);
    }
    catch (error) {
        clearRecordingState();
        return;
    }
    if (!info || typeof info.start !== 'number') {
        clearRecordingState();
        return;
    }
    const elapsed = Date.now() - info.start;
    const state = getState();
    const activeSession = getActiveSession(state, JitsiRecordingConstants.mode.FILE);
    if (!activeSession) {
        return;
    }
    if (elapsed >= RECORDING_MAX_DURATION_MS) {
        console.log('[Recording Guard] Maximum recording limit reached (120 min), stopping recording automatically.');
        clearRecordingState();
        const conference = getCurrentConference(state);
        if (conference && activeSession.id) {
            conference.stopRecording(activeSession.id);
        }
        return;
    }
    // Additional check: stop recording if only 1 participant (and not vconnect)
    if (!window.APP?.room?.vconnect && getParticipantCount(state) < 2) {
        console.log('[Recording Guard] Only one participant left in meeting, stopping recording automatically.');
        clearRecordingState();
        const conference = getCurrentConference(state);
        if (conference && activeSession.id) {
            conference.stopRecording(activeSession.id);
        }
        return;
    }
}
function restoreRecordingState(dispatch, getState, conference) {
    if (typeof localStorage === 'undefined') {
        return;
    }
    const meetingId = getMeetingIdSafely(conference);
    const key = getRecordingStorageKey(meetingId);
    const raw = localStorage.getItem(key);
    if (!raw) {
        return;
    }
    try {
        const info = JSON.parse(raw);
        if (!info || typeof info.start !== 'number') {
            localStorage.removeItem(key);
            return;
        }
        activeRecordingKey = key;
        console.log(`[Recording Guard] Restored state for key: ${key}`);
        startRecordingGuard(dispatch, getState);
        ensureRecordingWithinLimit(dispatch, getState);
    }
    catch (error) {
        console.warn('Unable to restore recording start payload', error);
        localStorage.removeItem(key);
    }
}
function forgetRecordingState() {
    clearRecordingState();
}
/**
 * Helper to check and execute 11sight auto-recording logic.
 */
function _checkAutoRecording(dispatch, getState, isParticipantLeft = false) {
    const state = getState();
    const conference = getCurrentConference(state);
    if (!conference) {
        return;
    }
    const isMod = isLocalParticipantModerator(state);
    const conferenceOptions = window.APP?.room?.conference_options;
    const autoRecordingEnabled = conferenceOptions?.auto_recording;
    const remainingRecordingLimit = conferenceOptions?.remaining_recording_limit;
    const isAuditor = window.APP?.isMeetingAuditor;
    console.log('[Recording Guard] _checkAutoRecording diagnostics:', {
        isModerator: isMod,
        autoRecordingEnabled,
        remainingRecordingLimit,
        isAuditor,
        participantCount: getParticipantCount(state)
    });
    if (!isMod || !autoRecordingEnabled || isAuditor) {
        return;
    }
    const _fileRecordingSession = getActiveSession(state, JitsiRecordingConstants.mode.FILE);
    const participantCount = getParticipantCount(state);
    // Auto-start recording
    const isVconnect = Boolean(window.APP?.room?.vconnect);
    const hasEnoughParticipants = isVconnect || participantCount >= 2;
    if (!_fileRecordingSession && hasEnoughParticipants && remainingRecordingLimit !== 0) {
        // Manual stop protection check
        let isManuallyStopped = false;
        try {
            isManuallyStopped = localStorage.getItem('__11sight_recording_manually_stopped') === 'true';
        }
        catch (e) { }
        if (isManuallyStopped) {
            console.log('[Recording Guard] Auto-start bypassed because recording was manually stopped.');
            return;
        }
        // Cooldown to prevent spamming start commands
        if (!window.__IISIGHT_AUTO_START_COOLDOWN) {
            console.log('[Recording Guard] Conditions met for auto-start. Starting recording in 1 second.');
            window.__IISIGHT_AUTO_START_COOLDOWN = true;
            setTimeout(() => {
                window.__IISIGHT_AUTO_START_COOLDOWN = false;
            }, 5000); // 5s cooldown
            setTimeout(() => {
                const freshState = getState();
                const freshSession = getActiveSession(freshState, JitsiRecordingConstants.mode.FILE);
                const freshCount = getParticipantCount(freshState);
                const freshVconnect = Boolean(window.APP?.room?.vconnect);
                const freshHasEnough = freshVconnect || freshCount >= 2;
                let isStillManuallyStopped = false;
                try {
                    isStillManuallyStopped = localStorage.getItem('__11sight_recording_manually_stopped') === 'true';
                }
                catch (e) { }
                if (!freshSession && freshHasEnough && !isStillManuallyStopped) {
                    console.log('[Recording Guard] Dispatching startRecording for auto-start.');
                    const metadata = get11SightRecordingMetadata(freshState);
                    const appData = JSON.stringify(metadata);
                    conference.startRecording({
                        mode: JitsiRecordingConstants.mode.FILE,
                        appData
                    });
                }
                else {
                    console.log('[Recording Guard] Auto-start check cancelled after delay settle.');
                }
            }, 1000);
        }
    }
    // Auto-stop recording
    if (isParticipantLeft) {
        if (_fileRecordingSession && participantCount < 2 && !isVconnect) {
            console.log('[Recording Guard] Only one participant left, auto-stopping recording.');
            if (_fileRecordingSession.id) {
                window.__11sight_auto_stopping = true;
                conference.stopRecording(_fileRecordingSession.id);
                setTimeout(() => {
                    window.__11sight_auto_stopping = false;
                }, 2000);
            }
        }
    }
}
/**
 * StateListenerRegistry provides a reliable way to detect the leaving of a
 * conference, where we need to clean up the recording sessions.
 */
StateListenerRegistry.register(
/* selector */ state => getCurrentConference(state), 
/* listener */ (conference, { dispatch, getState }) => {
    if (!conference) {
        try {
            localStorage.removeItem('__11sight_recording_manually_stopped');
        }
        catch (e) { }
        forgetRecordingState();
        dispatch(clearRecordingSessions());
    }
});
/**
 * The redux middleware to handle the recorder updates in a React way.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    let oldSessionData;
    if (action.type === RECORDING_SESSION_UPDATED) {
        oldSessionData
            = getSessionById(getState(), action.sessionData.id);
    }
    const result = next(action);
    switch (action.type) {
        case APP_WILL_MOUNT:
            registerRecordingAudioFiles(dispatch);
            break;
        case APP_WILL_UNMOUNT:
            forgetRecordingState();
            unregisterRecordingAudioFiles(dispatch);
            break;
        case CONFERENCE_JOIN_IN_PROGRESS: {
            const { conference } = action;
            try {
                localStorage.removeItem('__11sight_recording_manually_stopped');
                localStorage.removeItem('__11sight_recording_stopped_session_id');
                localStorage.removeItem('__11sight_livestreaming_manually_stopped');
                localStorage.removeItem('__11sight_livestreaming_stopped_session_id');
                console.log('[Recording Guard] Cleared leftover manual stop flags on conference join in progress.');
            }
            catch (e) { }
            restoreRecordingState(dispatch, getState, conference);
            conference.on(JitsiConferenceEvents.RECORDER_STATE_CHANGED, (recorderSession) => {
                if (recorderSession) {
                    recorderSession.getID() && dispatch(updateRecordingSessionData(recorderSession));
                    if (recorderSession.getError()) {
                        _showRecordingErrorNotification(recorderSession, dispatch, getState);
                    }
                    else {
                        _showExplicitConsentDialog(recorderSession, dispatch, getState);
                    }
                }
                return;
            });
            break;
        }
        case CONFERENCE_LEFT: {
            try {
                localStorage.removeItem('__11sight_recording_manually_stopped');
                localStorage.removeItem('__11sight_recording_stopped_session_id');
                localStorage.removeItem('__11sight_livestreaming_manually_stopped');
                localStorage.removeItem('__11sight_livestreaming_stopped_session_id');
            }
            catch (e) { }
            forgetRecordingState();
            break;
        }
        case 'START_RECORDING':
        case 'START_RECORDING': {
            try {
                localStorage.removeItem('__11sight_recording_manually_stopped');
                localStorage.removeItem('__11sight_recording_stopped_session_id');
                console.log('[Recording Guard] Cleared manual stop flag and stopped session ID in localStorage.');
            }
            catch (e) { }
            const state = getState();
            const currentConference = getCurrentConference(state);
            if (currentConference) {
                const metadata = get11SightRecordingMetadata(state);
                const appData = JSON.stringify(metadata);
                currentConference.startRecording({
                    mode: JitsiRecordingConstants.mode.FILE,
                    appData
                });
            }
            else {
                console.error('No active conference found to start recording.');
            }
            break;
        }
        case 'STOP_RECORDING':
        case 'STOP_RECORDING': {
            try {
                localStorage.setItem('__11sight_recording_manually_stopped', 'true');
                console.log('[Recording Guard] Set manual stop flag in localStorage.');
            }
            catch (e) { }
            const state = getState();
            const _fileRecordingSession = getActiveSession(state, JitsiRecordingConstants.mode.FILE);
            const currentConference = getCurrentConference(state);
            if (currentConference) {
                if (_fileRecordingSession?.id) {
                    currentConference.stopRecording(_fileRecordingSession.id);
                }
                else {
                    console.error('No active recording session ID found to stop recording.');
                }
            }
            else {
                console.error('No active conference found to stop recording.');
            }
            forgetRecordingState();
            break;
        }
        case START_LOCAL_RECORDING: {
            const { localRecording } = getState()['features/base/config'];
            const { onlySelf } = action;
            LocalRecordingManager.startLocalRecording({
                dispatch,
                getState
            }, action.onlySelf)
                .then(() => {
                const props = {
                    descriptionKey: 'recording.on',
                    titleKey: 'dialog.recording'
                };
                if (localRecording?.notifyAllParticipants && !onlySelf) {
                    dispatch(playSound(RECORDING_ON_SOUND_ID));
                }
                dispatch(showNotification(props, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                dispatch(showNotification({
                    titleKey: 'recording.localRecordingStartWarningTitle',
                    descriptionKey: 'recording.localRecordingStartWarning'
                }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
                dispatch(updateLocalRecordingStatus(true, onlySelf));
                sendAnalytics(createRecordingEvent('started', `local${onlySelf ? '.self' : ''}`));
                if (typeof APP !== 'undefined') {
                    APP.API.notifyRecordingStatusChanged(true, 'local', undefined, isRecorderTranscriptionsRunning(getState()));
                }
            })
                .catch(err => {
                logger.error('Capture failed', err);
                let descriptionKey = 'recording.error';
                if (err.message === 'WrongSurfaceSelected') {
                    descriptionKey = 'recording.surfaceError';
                }
                else if (err.message === 'NoLocalStreams') {
                    descriptionKey = 'recording.noStreams';
                }
                else if (err.message === 'NoMicTrack') {
                    descriptionKey = 'recording.noMicPermission';
                }
                const props = {
                    descriptionKey,
                    titleKey: 'recording.failedToStart'
                };
                if (typeof APP !== 'undefined') {
                    APP.API.notifyRecordingStatusChanged(false, 'local', err.message, isRecorderTranscriptionsRunning(getState()));
                }
                dispatch(showErrorNotification(props));
            });
            break;
        }
        case STOP_LOCAL_RECORDING: {
            const { localRecording } = getState()['features/base/config'];
            if (LocalRecordingManager.isRecordingLocally()) {
                LocalRecordingManager.stopLocalRecording();
                dispatch(updateLocalRecordingStatus(false));
                if (localRecording?.notifyAllParticipants && !LocalRecordingManager.selfRecording) {
                    dispatch(playSound(RECORDING_OFF_SOUND_ID));
                }
                if (typeof APP !== 'undefined') {
                    APP.API.notifyRecordingStatusChanged(false, 'local', undefined, isRecorderTranscriptionsRunning(getState()));
                }
            }
            break;
        }
        case RECORDING_SESSION_UPDATED: {
            const state = getState();
            // When in recorder mode no notifications are shown
            // or extra sounds are also not desired
            // but we want to indicate those in case of sip gateway
            const { iAmRecorder, iAmSipGateway, recordingLimit } = state['features/base/config'];
            if (iAmRecorder && !iAmSipGateway) {
                break;
            }
            const updatedSessionData = getSessionById(state, action.sessionData.id);
            const { initiator, mode = '', terminator } = updatedSessionData ?? {};
            const { PENDING, OFF, ON } = JitsiRecordingConstants.status;
            const isRecordingStarting = updatedSessionData?.status === PENDING && oldSessionData?.status !== PENDING;
            if (isRecordingStarting || updatedSessionData?.status === ON) {
                dispatch(hideNotification(START_RECORDING_NOTIFICATION_ID));
            }
            if (updatedSessionData?.status === ON) {
                try {
                    const currentId = updatedSessionData?.id;
                    if (currentId && typeof currentId === 'string') {
                        if (mode === JitsiRecordingConstants.mode.STREAM) {
                            const stoppedId = localStorage.getItem('__11sight_livestreaming_stopped_session_id');
                            if (!stoppedId || currentId !== stoppedId) {
                                localStorage.removeItem('__11sight_livestreaming_manually_stopped');
                                localStorage.removeItem('__11sight_livestreaming_stopped_session_id');
                            }
                        }
                        else {
                            const stoppedId = localStorage.getItem('__11sight_recording_stopped_session_id');
                            if (!stoppedId || currentId !== stoppedId) {
                                localStorage.removeItem('__11sight_recording_manually_stopped');
                                localStorage.removeItem('__11sight_recording_stopped_session_id');
                            }
                        }
                    }
                }
                catch (e) { }
            }
            if (isRecordingStarting) {
                dispatch(showPendingRecordingNotification(mode));
                break;
            }
            dispatch(hidePendingRecordingNotification(mode));
            if (updatedSessionData?.status === ON) {
                // We receive 2 updates of the session status ON. The first one is from jibri when it joins.
                // The second one is from jicofo which will deliver the initiator value. Since the start
                // recording notification uses the initiator value we skip the jibri update and show the
                // notification on the update from jicofo.
                // FIXME: simplify checks when the backend start sending only one status ON update containing
                // the initiator.
                if (initiator && !oldSessionData?.initiator) {
                    if (typeof recordingLimit === 'object') {
                        dispatch(showRecordingLimitNotification(mode));
                    }
                    else {
                        dispatch(showStartedRecordingNotification(mode, initiator, action.sessionData.id));
                    }
                }
                if (oldSessionData?.status !== ON) {
                    if (mode === JitsiRecordingConstants.mode.FILE) {
                        registerRecordingStart(dispatch, getState, updatedSessionData?.id);
                    }
                    sendAnalytics(createRecordingEvent('start', mode));
                    let soundID;
                    if (mode === JitsiRecordingConstants.mode.FILE && !isRecorderTranscriptionsRunning(state)) {
                        soundID = RECORDING_ON_SOUND_ID;
                    }
                    else if (mode === JitsiRecordingConstants.mode.STREAM) {
                        soundID = LIVE_STREAMING_ON_SOUND_ID;
                    }
                    if (soundID) {
                        dispatch(playSound(soundID));
                    }
                    if (typeof APP !== 'undefined') {
                        APP.API.notifyRecordingStatusChanged(true, mode, undefined, isRecorderTranscriptionsRunning(state));
                    }
                }
            }
            else if (updatedSessionData?.status === OFF && oldSessionData?.status !== OFF) {
                if (mode === JitsiRecordingConstants.mode.FILE) {
                    forgetRecordingState();
                }
                try {
                    if (mode === JitsiRecordingConstants.mode.STREAM) {
                        localStorage.setItem('__11sight_livestreaming_manually_stopped', 'true');
                        if (updatedSessionData?.id) {
                            localStorage.setItem('__11sight_livestreaming_stopped_session_id', updatedSessionData.id);
                        }
                        console.log('[Recording Guard] Set livestream manually stopped flag and ID on OFF status transition.');
                    }
                    else {
                        localStorage.setItem('__11sight_recording_manually_stopped', 'true');
                        if (updatedSessionData?.id) {
                            localStorage.setItem('__11sight_recording_stopped_session_id', updatedSessionData.id);
                        }
                        console.log('[Recording Guard] Set manually stopped flag and ID on OFF status transition.');
                    }
                }
                catch (e) { }
                dispatch(hideNotification(RECORDING_ANNOUNCEMENT_NOTIFICATION_ID));
                const terminatorName = terminator ? getParticipantDisplayName(state, getResourceId(terminator)) : undefined;
                dispatch(showStoppedRecordingNotification(mode, terminatorName));
                let duration = 0, soundOff, soundOn;
                if (oldSessionData?.timestamp) {
                    duration
                        = (Date.now() / 1000) - oldSessionData.timestamp;
                }
                sendAnalytics(createRecordingEvent('stop', mode, duration));
                if (mode === JitsiRecordingConstants.mode.FILE && !isRecorderTranscriptionsRunning(state)) {
                    soundOff = RECORDING_OFF_SOUND_ID;
                    soundOn = RECORDING_ON_SOUND_ID;
                }
                else if (mode === JitsiRecordingConstants.mode.STREAM) {
                    soundOff = LIVE_STREAMING_OFF_SOUND_ID;
                    soundOn = LIVE_STREAMING_ON_SOUND_ID;
                }
                if (soundOff && soundOn) {
                    dispatch(stopSound(soundOn));
                    dispatch(playSound(soundOff));
                }
                if (typeof APP !== 'undefined') {
                    APP.API.notifyRecordingStatusChanged(false, mode, undefined, isRecorderTranscriptionsRunning(state));
                }
            }
            break;
        }
        case TRACK_ADDED: {
            const { track } = action;
            if (LocalRecordingManager.isRecordingLocally()
                && track.mediaType === MEDIA_TYPE.AUDIO && track.local) {
                const audioTrack = track.jitsiTrack.track;
                LocalRecordingManager.addAudioTrackToLocalRecording(audioTrack);
            }
            _checkAutoRecording(dispatch, getState);
            break;
        }
        case PARTICIPANT_UPDATED: {
            const { id, role } = action.participant;
            const state = getState();
            const localParticipant = getLocalParticipant(state);
            if (localParticipant?.id === id && role === PARTICIPANT_ROLE.MODERATOR) {
                dispatch(showStartRecordingNotification());
            }
            const result = next(action);
            _checkAutoRecording(dispatch, getState);
            return result;
        }
        case PARTICIPANT_JOINED: {
            const result = next(action);
            _checkAutoRecording(dispatch, getState);
            return result;
        }
        case PARTICIPANT_LEFT: {
            const result = next(action);
            _checkAutoRecording(dispatch, getState, true);
            return result;
        }
    }
    return result;
});
/**
 * Shows a notification about an error in the recording session. A
 * default notification will display if no error is specified in the passed
 * in recording session.
 *
 * @private
 * @param {Object} session - The recorder session model from the
 * lib.
 * @param {Dispatch} dispatch - The Redux Dispatch function.
 * @param {Function} getState - The Redux getState function.
 * @returns {void}
 */
function _showRecordingErrorNotification(session, dispatch, getState) {
    const mode = session.getMode();
    const error = session.getError();
    const isStreamMode = mode === JitsiMeetJS.constants.recording.mode.STREAM;
    switch (error) {
        case JitsiMeetJS.constants.recording.error.SERVICE_UNAVAILABLE:
            dispatch(showRecordingError({
                descriptionKey: 'recording.unavailable',
                descriptionArguments: {
                    serviceName: isStreamMode
                        ? '$t(liveStreaming.serviceName)'
                        : '$t(recording.serviceName)'
                },
                titleKey: isStreamMode
                    ? 'liveStreaming.unavailableTitle'
                    : 'recording.unavailableTitle'
            }));
            break;
        case JitsiMeetJS.constants.recording.error.RESOURCE_CONSTRAINT:
            dispatch(showRecordingError({
                descriptionKey: isStreamMode
                    ? 'liveStreaming.busy'
                    : 'recording.busy',
                titleKey: isStreamMode
                    ? 'liveStreaming.busyTitle'
                    : 'recording.busyTitle'
            }));
            break;
        case JitsiMeetJS.constants.recording.error.UNEXPECTED_REQUEST:
            dispatch(showRecordingWarning({
                descriptionKey: isStreamMode
                    ? 'liveStreaming.sessionAlreadyActive'
                    : 'recording.sessionAlreadyActive',
                titleKey: isStreamMode ? 'liveStreaming.inProgress' : 'recording.inProgress'
            }));
            break;
        case JitsiMeetJS.constants.recording.error.POLICY_VIOLATION:
            dispatch(showRecordingWarning({
                descriptionKey: isStreamMode ? 'liveStreaming.policyError' : 'recording.policyError',
                titleKey: isStreamMode ? 'liveStreaming.failedToStart' : 'recording.failedToStart'
            }));
            break;
        default:
            dispatch(showRecordingError({
                descriptionKey: isStreamMode
                    ? 'liveStreaming.error'
                    : 'recording.error',
                titleKey: isStreamMode
                    ? 'liveStreaming.failedToStart'
                    : 'recording.failedToStart'
            }));
            break;
    }
    if (typeof APP !== 'undefined') {
        APP.API.notifyRecordingStatusChanged(false, mode, error, isRecorderTranscriptionsRunning(getState()));
    }
}
/**
 * Mutes audio and video and displays the RecordingConsentDialog when the conditions are met.
 *
 * @param {any} recorderSession - The recording session.
 * @param {Function} dispatch - The Redux dispatch function.
 * @param {Function} getState - The Redux getState function.
 * @returns {void}
 */
function _showExplicitConsentDialog(recorderSession, dispatch, getState) {
    if (!shouldRequireRecordingConsent(recorderSession, getState())) {
        return;
    }
    batch(() => {
        dispatch(markConsentRequested(recorderSession.getID()));
        dispatch(setAudioUnmutePermissions(true, true));
        dispatch(setVideoUnmutePermissions(true, true));
        dispatch(setAudioMuted(true));
        dispatch(setVideoMuted(true));
        dispatch(openDialog('RecordingConsentDialog', RecordingConsentDialog));
    });
}
